import React, { useEffect, useRef, useState } from 'react';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import './LogoCropInput.css';

// Cote long du logo exporte — le backend le redimensionne deja pour l'impression
// (companies/serializers.py:_logo_to_base64, 300px), donc pas besoin d'un fichier
// plus grand que ca meme si l'utilisateur choisit une photo tres haute resolution.
const OUTPUT_MAX_SIZE = 480;

const ASPECT_OPTIONS = [
  { label: 'Libre', value: 0 },
  { label: 'Carre (1:1)', value: 1 },
  { label: 'Large (2:1)', value: 2 },
];

// Champ logo reutilisable (upload + recadrage) pour CompanyProfilePage (self-service
// company) et SuperadminCompanyFormPage (superadmin). `onChange` recoit un Blob PNG
// deja recadre, pret a etre ajoute a un FormData sous la cle "logo".
//
// Cropper.js (v1, l'API imperative "classique", pas la v2 web-components recente)
// plutot que react-image-crop : celui-ci recreait un TOUT PETIT nouveau cadre de
// recadrage des qu'on cliquait/glissait legerement a cote d'une poignee, ecrasant
// le recadrage en cours (signale explicitement par l'utilisateur). Avec
// `dragMode:'move'`, un cadre unique et persistant existe toujours — glisser
// A L'INTERIEUR le deplace, glisser SES poignees le redimensionne, glisser EN
// DEHORS ne fait que deplacer l'image dessous (jamais de nouveau cadre surprise).
const LogoCropInput = ({ currentUrl, onChange }) => {
  const [rawSrc, setRawSrc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [aspectValue, setAspectValue] = useState(ASPECT_OPTIONS[0].value);
  const [saving, setSaving] = useState(false);
  const imgRef = useRef(null);
  const cropperRef = useRef(null);

  // Une seule instanciation par image choisie — le changement d'aspect ensuite
  // passe par cropper.setAspectRatio() (imperatif) pour ne PAS perdre le cadrage
  // deja en cours en recreant l'instance a chaque changement de select.
  useEffect(() => {
    if (!rawSrc || !imgRef.current) return undefined;
    const cropper = new Cropper(imgRef.current, {
      viewMode: 1,
      dragMode: 'move',
      autoCropArea: 0.9,
      movable: true,
      zoomable: true,
      rotatable: false,
      scalable: false,
      cropBoxMovable: true,
      cropBoxResizable: true,
      toggleDragModeOnDblclick: false,
      guides: true,
      center: true,
      aspectRatio: aspectValue || NaN,
    });
    cropperRef.current = cropper;
    return () => {
      cropper.destroy();
      cropperRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSrc]);

  const handleFilePicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setRawSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const handleAspectChange = (value) => {
    setAspectValue(value);
    cropperRef.current?.setAspectRatio(value || NaN);
  };

  const handleCancelCrop = () => setRawSrc(null);

  const handleValidateCrop = async () => {
    const cropper = cropperRef.current;
    if (!cropper) return;
    setSaving(true);
    try {
      const canvas = cropper.getCroppedCanvas({
        maxWidth: OUTPUT_MAX_SIZE,
        maxHeight: OUTPUT_MAX_SIZE,
        imageSmoothingQuality: 'high',
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      setPreviewUrl(URL.createObjectURL(blob));
      onChange(blob);
      setRawSrc(null);
    } finally {
      setSaving(false);
    }
  };

  const shownPreview = previewUrl || currentUrl;

  return (
    <div className="lci-wrap">
      <div className="lci-preview-col">
        {shownPreview
          ? <img src={shownPreview} alt="Logo" className="lci-preview" />
          : <div className="lci-preview lci-preview-empty">Pas de logo</div>}
        <label className="lci-pick-btn">
          Choisir une image...
          <input type="file" accept="image/*" onChange={handleFilePicked} hidden />
        </label>
      </div>

      {rawSrc && (
        <div className="lci-modal-overlay">
          <div className="lci-modal">
            <div className="lci-modal-header">
              <span>Recadrer le logo</span>
              <select
                className="lci-aspect-select"
                value={aspectValue}
                onChange={(e) => handleAspectChange(Number(e.target.value))}
              >
                {ASPECT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="lci-crop-area">
              {/* eslint-disable-next-line jsx-a11y/alt-text */}
              <img ref={imgRef} src={rawSrc} className="lci-crop-img" />
            </div>

            <p className="lci-hint">
              Glisse a l'interieur du cadre pour le deplacer, ses poignees pour le
              redimensionner, ou en dehors pour deplacer l'image.
            </p>

            <div className="lci-modal-actions">
              <button type="button" className="lci-btn lci-btn-cancel" onClick={handleCancelCrop}>
                Annuler
              </button>
              <button
                type="button"
                className="lci-btn lci-btn-confirm"
                onClick={handleValidateCrop}
                disabled={saving}
              >
                {saving ? 'Traitement...' : 'Valider le recadrage'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogoCropInput;
